import { useState } from 'react';
import { useDesigner } from '../../context/DesignerContext';
import { TokenSlider } from '../TokenSlider';
import styles from './TokenPanel.module.css';

export interface TokenPanelProps {
  className?: string;
}

/**
 * Grouped token editor panel (R3).
 *
 * Renders all tokens from the registry grouped by category, with a search
 * filter. Each token renders as a TokenSlider. Groups with no matching
 * tokens are hidden.
 */
export function TokenPanel({ className }: TokenPanelProps) {
  const { registry } = useDesigner();
  const [query, setQuery] = useState('');

  const groupNames = registry.getGroupNames();
  const filteredTokens = query
    ? registry.search(query)
    : registry.getAllTokens();

  const filteredCssVars = new Set(filteredTokens.map((t) => t.path.cssVar));

  return (
    <div
      data-crd-id="r3.bq-designer-token-panel"
      data-crd-layer="r3"
      className={`${styles.panel} ${className ?? ''}`}
    >
      <div className={styles.searchWrap}>
        <input
          type="search"
          className={styles.search}
          placeholder="Search tokens..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search tokens"
        />
      </div>

      <div className={styles.groups}>
        {groupNames.map((groupName) => {
          const groupTokens = registry
            .getGroup(groupName)
            .filter((t) => filteredCssVars.has(t.path.cssVar));

          if (groupTokens.length === 0) return null;

          return (
            <div key={groupName} className={styles.group}>
              <h3 className={styles.groupTitle}>{groupName}</h3>
              <div className={styles.tokens}>
                {groupTokens.map((token) => (
                  <TokenSlider key={token.path.cssVar} token={token} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
